import React, { useCallback, useEffect, useState } from "react";

import * as Card from "@components/card/index";
import { CardProps } from "@components/card/types";
import { Component as Grid } from "@components/grid/index";
import { Skeleton } from "@components/skeleton/index";
import { DaperNewsletter } from "@data/daper-client/types";
import { IntercomTracking } from "@interface/IntercomSlot";
import type {
  IntercomComponentProps,
  IntercomInterface,
} from "@interface/types";
import { truthyFilter } from "@util/truthyFilter";

export type Props = {
  intercom: IntercomInterface;
  newsletterTitles?: string[];
} & IntercomComponentProps;

export function UserNewsletterStories(props: Props) {
  const { newsletterTitles } = props;

  const [isLoading, setIsLoading] = useState(false);
  const [componentPropsList, setComponentPropsList] = useState<
    CardProps[]
  >([]);

  const getSubscribedNewsletters = useCallback(
    async ({ lists }: { lists: string[] }) => {
      const newsletters = await props?.intercom.getNewsletters();

      return lists
        .map((title) =>
          newsletters.find(
            (newsletter) => newsletter?.attributes?.title === title
          )
        )
        .filter(truthyFilter);
    },
    []
  );

  const getStoriesFromNewsletterSlugs = useCallback(
    async (newsletters: DaperNewsletter[]) =>
      (
        await Promise.allSettled(
          newsletters.map((newsletter) =>
            props.intercom.getStories({
              "editorialTaxonomies.newsletterSlug": newsletter.attributes.slug,
            })
          )
        )
      )
        .map((response) =>
          response.status === "fulfilled" ? response.value : null
        )
        .filter(truthyFilter)
        .flat(),
    []
  );

  const getImages = useCallback(
    (id: string) => props?.intercom.getImages({ id }),
    []
  );

  const getStoriesWithImages = useCallback(async ({ lists }) => {
    const newsletters = await getSubscribedNewsletters({ lists });
    const stories = await getStoriesFromNewsletterSlugs(newsletters);

    const responses = await Promise.allSettled(
      stories
        .map(async (story) => {
          const ledeImageId = story?.extraMedia?.lede?.default.id;

          if (!ledeImageId) return null;

          const images = await getImages(ledeImageId);

          return { ...story, images };
        })
        .filter((r) => r)
    );

    return responses
      .map((response) =>
        response.status === "fulfilled" ? response.value : null
      )
      .filter(truthyFilter);
  }, []);

  useEffect(() => {
    setIsLoading(true);
    getStoriesWithImages({ lists: newsletterTitles }).then((stories) => {
      const propsList = stories.map((story) => ({
        eyebrow: {
          text: story.label,
        },
        image: {
          assetUrl: story.images.baseUrl,
          isLazy: true,
          aspectRatio: 1,
        },
        text: story.headlines.web,
        timestamp: story.publishedAt,
        description: story.summary.text,
        url: story.url,
      }));

      setComponentPropsList(propsList);
      setIsLoading(false);
    });
  }, [newsletterTitles]);

  const CardWithBordersAndSkeleton = (componentProps: CardProps) => (
    <Skeleton isLoading={isLoading}>
      <Card.WithFullBorderStyle {...componentProps} />
    </Skeleton>
  );

  return (
    <IntercomTracking componentName="UserNewsletterStories">
      <Grid
        IterableComponent={CardWithBordersAndSkeleton}
        componentPropsList={componentPropsList}
        numberOfRows={2}
        numberOfColumns={3}
      />
    </IntercomTracking>
  );
}
